import React from 'react'
import { useNavigate } from '@tanstack/react-router'
import { confirmMfaSetup, isApiError, tokenStore, verifyMfa, verifyOtp } from '../api'
import { completeLogin } from '../utils/complete-login'
import { Button } from './Button'
import { FormError } from './FormError'
import { useToast } from './Toast'

const OTP_LENGTH = 6

type OtpMode = 'email' | 'app'

interface OtpFormProps {
  /**
   * `"email"` — first-time email OTP (`POST /auth/otp/verify`).
   * `"app"`   — authenticator TOTP: confirms MFA setup for a first-time user
   *             (`/auth/mfa/setup/confirm`) or signs in a returning user
   *             (`/auth/mfa/verify`), depending on which token is in session.
   */
  mode: OtpMode
  /** The address the email OTP was sent to (required in `"email"` mode). */
  email?: string
  /**
   * Called in `"app"` mode once first-time MFA setup is confirmed, handing up
   * the one-time backup codes so the page can advance to the backup-codes step.
   */
  onMfaSetupComplete?: (backupCodes: Array<string>) => void
}

/**
 * Client-side 6-digit code entry shared by the email-verification and
 * authenticator-app steps.
 *
 * Owns the per-box digit state so the Verify button only enables once all
 * boxes are filled, and handles focus movement: typing a digit advances to the
 * next box, Backspace on an empty box steps back, arrow keys move between
 * boxes, and pasting a code distributes it across the boxes. On submit it calls
 * the endpoint matching {@link OtpFormProps.mode} and routes onward.
 */
export function OtpForm({ mode, email, onMfaSetupComplete }: OtpFormProps) {
  const navigate = useNavigate()
  const { showToast } = useToast()

  const [digits, setDigits] = React.useState<Array<string>>(() =>
    Array.from({ length: OTP_LENGTH }, () => ''),
  )
  const [submitting, setSubmitting] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)
  const inputRefs = React.useRef<Array<HTMLInputElement | null>>([])

  const isComplete = digits.every((digit) => digit !== '')

  const focusBox = (index: number) => {
    inputRefs.current[index]?.focus()
    inputRefs.current[index]?.select()
  }

  const resetDigits = () => {
    setDigits(Array.from({ length: OTP_LENGTH }, () => ''))
    focusBox(0)
  }

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>, index: number) => {
    // Keep only the last typed digit, ignoring any non-numeric character.
    const digit = event.target.value.replace(/\D/g, '').slice(-1)

    setDigits((prev) => {
      const next = [...prev]
      next[index] = digit
      return next
    })

    if (digit && index < OTP_LENGTH - 1) {
      focusBox(index + 1)
    }

    setError(null)
  }

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>, index: number) => {
    if (event.key === 'Backspace') {
      if (digits[index] === '' && index > 0) {
        // Nothing to clear here — step back to the previous box.
        event.preventDefault()
        focusBox(index - 1)
      }
      return
    }

    if (event.key === 'ArrowLeft' && index > 0) {
      event.preventDefault()
      focusBox(index - 1)
    } else if (event.key === 'ArrowRight' && index < OTP_LENGTH - 1) {
      event.preventDefault()
      focusBox(index + 1)
    }
  }

  const handlePaste = (event: React.ClipboardEvent<HTMLInputElement>, index: number) => {
    event.preventDefault()
    const pasted = event.clipboardData
      .getData('text')
      .replace(/\D/g, '')
      .slice(0, OTP_LENGTH - index)
    if (!pasted) return

    setDigits((prev) => {
      const next = [...prev]
      for (let i = 0; i < pasted.length; i += 1) {
        next[index + i] = pasted[i]
      }
      return next
    })

    const lastFilled = Math.min(index + pasted.length, OTP_LENGTH - 1)
    focusBox(lastFilled)
  }

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (!isComplete || submitting) return

    const code = digits.join('')
    setSubmitting(true)
    setError(null)

    try {
      if (mode === 'email') {
        const targetEmail = email ?? tokenStore.getPendingEmail()
        if (!targetEmail) {
          setError('Your session has expired. Please sign-in again.')
          setSubmitting(false)
          return
        }
        const result = await verifyOtp({ email: targetEmail, otp: code })
        // JWT that authorises the upcoming MFA setup.
        tokenStore.setAccessToken(result.token)
        navigate({ to: '/login/setup-authenticator' })
        return
      }

      // mode === "app": returning sign-in if we hold an mfaToken, otherwise
      // we're confirming first-time MFA setup with the JWT from otp/verify.
      const mfaToken = tokenStore.getMfaToken()
      if (mfaToken) {
        const result = await verifyMfa({ mfaToken, code })
        tokenStore.setAccessToken(result.token)
        tokenStore.clearMfaToken()
        showToast('Login success', 'success')
        setTimeout(() => completeLogin(), 2000)
        return
      }

      const accessToken = tokenStore.getAccessToken()
      if (accessToken) {
        const result = await confirmMfaSetup(code, accessToken)
        // Backup codes are returned once — hand them up so the page can advance
        // to the backup-codes step.
        setSubmitting(false)
        onMfaSetupComplete?.(result.backupCodes)
        return
      }

      setError('Your session has expired. Please sign-in again.')
      setSubmitting(false)
    } catch (err) {
      setError(isApiError(err) ? err.message : 'Something went wrong. Please try again.')
      setSubmitting(false)
      resetDigits()
    }
  }

  return (
    <form className="flex flex-col gap-6" id={`form-otp-${mode}`} onSubmit={handleSubmit}>
      <div>
        <div
          className="flex items-center gap-2 sm:gap-3"
          aria-label="Enter 6 digit verification code"
          role="group"
        >
          {digits.map((digit, index) => (
            <input
              key={index}
              ref={(element) => {
                inputRefs.current[index] = element
              }}
              type="text"
              id={`otp-box-${mode}-${index + 1}`}
              maxLength={1}
              pattern="[0-9]*"
              inputMode="numeric"
              autoComplete={index === 0 ? 'one-time-code' : 'off'}
              placeholder=""
              value={digit}
              disabled={submitting}
              onChange={(event) => handleChange(event, index)}
              onKeyDown={(event) => handleKeyDown(event, index)}
              onPaste={(event) => handlePaste(event, index)}
              className={`min-w-0 flex-1 h-14 text-center text-xl font-bold bg-white border rounded-lg focus:outline-none focus:ring-1 focus:ring-brand-cyan/50 focus:border-brand-cyan text-brand-dark-text transition-all disabled:opacity-50 ${
                error ? 'border-brand-border-error' : 'border-brand-border-light'
              }`}
            />
          ))}
        </div>

        {error && (
          <FormError id={`alert-otp-${mode}-error`} className="mt-2 text-center">
            {error}
          </FormError>
        )}
      </div>

      <Button type="submit" id={`btn-otp-${mode}-verify`} disabled={!isComplete || submitting}>
        {submitting ? 'Verifying…' : 'Verify code'}
      </Button>
    </form>
  )
}
